import {isReadableError} from '@/lib/api/ReadableError'
import throwIfError from '@/lib/api/throwIfError'
import cartDb from '@/lib/cart/cartDb'
import {
  getCurrencyFromLocale,
  getLanguageCountryFromLocale,
  localeUnderscoreToHyphen,
  type SupportedLocales,
} from '@/lib/locale/locale'
import {type Cart, type CartItem, parseCart, transformRawCartItem} from '@/models/cartModel'
import call from '@/utils/api/call'
import shouldLogError from '@/utils/api/shouldLogError'
import UnexpectedError from '@/utils/api/UnexpectedError'
import MaybePermanentError from '@/utils/errors/MaybePermanentError'
import UserFriendlyError from '@/utils/errors/UserFriendlyError'
import waitAtLeast from '@/utils/waitAtLeast'

const NON_ERROR_API_CODES = ['ICA0000']

export async function getCart({
  locale,
  signal,
  isAuthenticated,
}: {
  isAuthenticated: boolean
  locale: SupportedLocales
  signal?: AbortSignal
}) {
  const {country} = getLanguageCountryFromLocale(locale)

  if (!isAuthenticated) return waitAtLeast(Promise.resolve(cartDb.getCart(country)), 500)

  return call
    .get('/api/checkout/v1/carts', {
      signal,
      headers: {
        'Accept-Language': localeUnderscoreToHyphen(locale),
      },
    })
    .then(res => {
      throwIfError(res, NON_ERROR_API_CODES)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- its safe
      return parseCart(res.data.data)
    })
    .catch(e => {
      if (shouldLogError(e)) {
        console.error('Cannot fetch cart', e)
      }
      throw new UnexpectedError(e)
    })
}

// TODO: ask BE to handle this
export async function updateCartWith({
  signal,
  items,
  replace,
  locale,
  isAuthenticated,
}: {
  isAuthenticated: boolean
  items: CartItem[]
  replace?: boolean
  signal?: AbortSignal
  locale: SupportedLocales
}): Promise<Cart> {
  const currency = getCurrencyFromLocale(locale)

  if (!isAuthenticated) {
    const {country} = getLanguageCountryFromLocale(locale)

    return waitAtLeast(
      Promise.resolve(
        cartDb.updateCart(
          {
            country,
            items,
          },
          replace,
        ),
      ),
      500,
    )
  }

  const currentCart = await getCart({isAuthenticated, locale, signal})

  items.forEach(item => {
    const existItemIndex = currentCart.items.findIndex(i => {
      return i.id === item.id && i.retailer === item.retailer
    })

    if (item.quantity === 0) {
      // Remove item
      if (existItemIndex !== -1) {
        currentCart.items.splice(existItemIndex, 1)
      }
    } else if (existItemIndex !== -1) {
      // Add item
      const existItem = currentCart.items.at(existItemIndex)
      if (existItem) {
        currentCart.items[existItemIndex] = {
          ...item,
          quantity: !replace ? existItem.quantity + item.quantity : item.quantity, // Replace item
        }
      }
    } else {
      currentCart.items.unshift(item)
    }
  })

  return call
    .patch(
      '/api/checkout/v1/carts',
      {
        lineItems: currentCart.items.map(item => transformRawCartItem(item, currency)),
      },
      {
        signal,
        headers: {
          'Accept-Language': localeUnderscoreToHyphen(locale),
        },
      },
    )
    .then(res => {
      throwIfError(res, NON_ERROR_API_CODES)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- its safe
      return parseCart(res.data.data)
    })
    .catch(e => {
      if (isReadableError(e) && e.res.data.code === 'ICA1001') {
        // TODO: handle i18n
        throw new UnexpectedError(e).permanent()
      }

      if (shouldLogError(e)) {
        console.error('Cannot patch cart', e)
      }
      throw new UnexpectedError(e)
    })
}

// export async function checkout({
//   locale,
//   successHref,
//   failureHref,
//   queryClient,
//   applyRewards,
//   isAuthenticated,
// }: {
//   isAuthenticated: boolean
//   successHref: string
//   failureHref: string
//   locale: SupportedLocales
//   queryClient: QueryClient
//   applyRewards: boolean
// }) {
//   const currency = getCurrencyFromLocale(locale)

//   const {v4: uuidV4} = await import('uuid')

//   const uuid = uuidV4()

//   const willUseGuestCart = !isAuthenticated

//   const api = willUseGuestCart
//     ? '/api/checkout/pub/v1/carts/checkout'
//     : '/api/checkout/v1/carts/checkout'

//   const {country} = getLanguageCountryFromLocale(locale)

//   const data = willUseGuestCart
//     ? {
//         cartId: uuid,
//         checkoutType: 'STRIPE',
//         lineItems: cartDb.getCart(country).items.map(item => transformRawCartItem(item, currency)),
//         successUrl: location.origin + successHref,
//         cancelUrl: location.origin + failureHref,
//       }
//     : {
//         idempotencyKey: uuid,
//         checkoutType: 'STRIPE',
//         successUrl: location.origin + successHref,
//         cancelUrl: location.origin + failureHref,
//         useWallet: applyRewards,
//       }

//   return call
//     .post(api, data, {
//       headers: {
//         'Accept-Language': localeUnderscoreToHyphen(locale),
//       },
//     })
//     .then(res => {
//       const data = res.data as object
//       if ('code' in data && typeof data.code === 'string' && data.code === 'ICA1120') {
//         throw new CartCalibratedError(data)
//       }

//       throwIfError(res, NON_ERROR_API_CODES)
//       // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment -- its safe since we have catch
//       window.location.href = res.data.data.checkoutUrl
//       return true
//     })
//     .catch(e => {
//       if (shouldLogError(e)) {
//         console.error('Cannot checkout cart', e)
//       }
//       if (isCartCalibratedError(e)) {
//         if (willUseGuestCart) {
//           // PATCH cartDb
//           const currentCart = cartDb.getCart(country)
//           currentCart.items = currentCart.items
//             .filter(
//               item => !e.unavailableItems.some(unavailableItem => unavailableItem.id === item.id),
//             )
//             .map(item => {
//               if (e.priceChangedItems.some(priceChangedItem => priceChangedItem.id === item.id)) {
//                 item.price =
//                   e.priceChangedItems.find(priceChangedItem => priceChangedItem.id === item.id)
//                     ?.newPrice ?? item.price
//               }
//               return item
//             })
//           cartDb.updateCart(currentCart, true)
//         }
//         // TODO: instead of refetching cart, we should show an advanced UI to the user and let them know about the old price, new price
//         void queryClient.refetchQueries({queryKey: queries.getQueryKey('cart', [locale])})
//         throw e
//       }
//       if (isReadableError(e)) {
//         throw new UserFriendlyError(e.res.data.msg as string, e)
//       }
//       throw new UnexpectedError(e)
//     })
// }

export class CartCalibratedError extends MaybePermanentError {
  unavailableItems: {
    id: string
    title: string
  }[] = []
  priceChangedItems: {
    id: string
    title: string
    newPrice: number
    currency: string
  }[] = []

  constructor(cause?: unknown) {
    super(undefined, {cause})
    this.name = 'CartCalibratedError'
    this.permanent()

    /* eslint-disable @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access -- validation later */
    this.unavailableItems =
      /* @ts-expect-error -- validation later */
      cause.data.lineItemErrors.LINE_ITEM_UNAVAILABLE?.map(item => ({
        id: item.itemId,
        title: item.title,
      })) ?? []
    this.priceChangedItems =
      /* @ts-expect-error -- validation later */
      cause.data.lineItemErrors.LINE_ITEM_PRICE_CHANGED?.map(item => ({
        id: item.itemId,
        title: item.title,
        newPrice: Number(item.price),
        currency: item.currency,
      })) ?? []
    /* eslint-enable @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access */
  }
}

export function isCartCalibratedError(e: unknown): e is CartCalibratedError {
  return e instanceof CartCalibratedError
}

export function fixCartItems(items: CartItem[], error: CartCalibratedError) {
  return items
    .filter(item => !error.unavailableItems.some(unavailableItem => unavailableItem.id === item.id))
    .map(item => {
      if (error.priceChangedItems.some(priceChangedItem => priceChangedItem.id === item.id)) {
        item.price =
          error.priceChangedItems.find(priceChangedItem => priceChangedItem.id === item.id)
            ?.newPrice ?? item.price
      }
      return item
    })
}

export function buildStringForCalibratedError(error: CartCalibratedError) {
  const unavailables =
    error.unavailableItems.length > 0
      ? `The following items are unavailable: ${error.unavailableItems.map(item => item.title).join(', ')}`
      : false
  const priceChanged =
    error.priceChangedItems.length > 0
      ? `The following items have changed price: ${error.priceChangedItems.map(item => `${item.title}: changed to (${item.newPrice} ${item.currency})`).join(', ')}`
      : false
  return [unavailables, priceChanged].filter(Boolean).join('\n')
}

export async function buyNow({
  items,
  locale,
  successHref,
  failureHref,
  applyRewards,
  isAuthenticated,
}: {
  isAuthenticated: boolean
  items: CartItem[]
  successHref: string
  failureHref: string
  locale: SupportedLocales
  applyRewards: boolean
}) {
  const currency = getCurrencyFromLocale(locale)

  const api = isAuthenticated
    ? '/api/checkout/v1/carts/checkout-line-items'
    : '/api/checkout/pub/v1/carts/checkout'

  const data = isAuthenticated
    ? {
        checkoutType: 'STRIPE',
        lineItems: items.map(item => transformRawCartItem(item, currency)),
        successUrl: globalThis.location.origin + successHref,
        cancelUrl: globalThis.location.origin + failureHref,
        useWallet: applyRewards,
      }
    : await (async () => {
        const {v4: uuidV4} = await import('uuid')

        const uuid = uuidV4()
        return {
          cartId: uuid,
          checkoutType: 'STRIPE',
          lineItems: items.map(item => transformRawCartItem(item, currency)),
          successUrl: globalThis.location.origin + successHref,
          cancelUrl: globalThis.location.origin + failureHref,
        }
      })()

  return call
    .post(api, data, {
      headers: {
        'Accept-Language': localeUnderscoreToHyphen(locale),
      },
    })
    .then(res => {
      throwIfError(res, NON_ERROR_API_CODES)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- its safe since we have catch
      window.location.href = res.data.data.checkoutUrl
      return true
    })
    .catch(e => {
      if (shouldLogError(e)) {
        console.error('Cannot checkout line item', e)
      }
      if (isReadableError(e)) {
        throw new UserFriendlyError(e.res.data.msg as string, e)
      }
      throw new UnexpectedError(e)
    })
}
